import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";

import "./EnviarNotificacao.css";

const MODE_OPTIONS = [
  { value: "user", label: "A) 1 usuário específico" },
  { value: "topic", label: "B) Todos os usuários (Broadcast por tópico)" },
  { value: "segment", label: "C) Segmentado" },
];

const SEGMENT_PRESETS = [
  { value: "verified_only", label: "✅ Somente verificados" },
  { value: "premium_only", label: "⭐ Somente premium" },
  { value: "inactive_days", label: "⏳ Inativos há X dias" },
];

// ✅ Deep-link / extras que vão no FCM "data"
const ACTION_OPTIONS = [
  { value: "none", label: "🔔 Apenas abrir tela de Notificações (padrão)" },
  { value: "chat", label: "💌 Abrir Chat (type=chat)" },
  { value: "match", label: "💞 Abrir Match (type=match)" },
  { value: "like", label: "👀 Abrir Curtida (type=like)" },
  { value: "verified", label: "✅ Perfil Verificado (type=verified)" },
  { value: "suggestion", label: "✨ Abrir Sugestão (type=suggestion)" },
  { value: "custom", label: "⚙️ Custom (JSON livre)" },
];

/**
 * ⚡ Templates rápidos (do sistema)
 * - key: id estável do template
 * - title/message: texto do push
 * - actionType: seta automaticamente o deep-link
 * - customData: (opcional) quando actionType === "custom"
 *
 * Observação: chat/match/like/suggestion podem exigir IDs (matchId / otherUserId / fromUserId / suggestionId).
 * Nesses casos, o usuário completa na UI antes de enviar.
 */
const QUICK_TEMPLATES = [
  // =========================
  // One Love (relacionamento)
  // =========================
  {
    key: "welcome",
    title: "💗 Bem-vindo(a) ao One Love!",
    message:
      "Seu perfil está pronto! Complete suas fotos e comece a curtir para encontrar conexões reais.",
    actionType: "none",
  },
  {
    key: "complete_profile",
    title: "📸 Complete seu perfil",
    message:
      "Perfis completos recebem mais curtidas. Adicione fotos e escreva uma bio para aumentar suas chances ✨",
    actionType: "custom",
    customData: { type: "custom", route: "/profile" },
  },
  {
    key: "new_like",
    title: "👀 Você recebeu uma curtida!",
    message:
      "Alguém curtiu você agora mesmo. Abra para ver quem foi e retribuir 😉",
    actionType: "like",
  },
  {
    key: "new_match",
    title: "💞 Match confirmado!",
    message:
      "Vocês curtiram um ao outro! Que tal mandar um oi e começar uma conversa? 💬",
    actionType: "match",
  },
  {
    key: "new_message",
    title: "💌 Nova mensagem!",
    message: "Você recebeu uma nova mensagem. Abra o chat para responder agora.",
    actionType: "chat",
  },
  {
    key: "new_suggestion",
    title: "✨ Nova sugestão disponível!",
    message:
      "A IA do Amor gerou novas sugestões para você. Abra e veja suas combinações da semana 💗",
    actionType: "suggestion",
  },
  {
    key: "verified_ok",
    title: "✅ Perfil verificado!",
    message:
      "Seu perfil foi verificado com sucesso! Agora você ganha mais confiança e destaque na plataforma.",
    actionType: "verified",
  },
  {
    key: "inactive_return",
    title: "⏳ Sentimos sua falta!",
    message:
      "Tem novidades te esperando no One Love. Volte e veja suas curtidas, matches e mensagens 💗",
    actionType: "none",
  },
  {
    key: "safety_tip",
    title: "🛡️ Dica de segurança",
    message:
      "Para sua segurança, evite enviar dados pessoais logo no início. Use o chat do app e vá com calma ✨",
    actionType: "none",
  },

  // =========================
  // Produtos / Loja (gatilhos mentais "limpos")
  // =========================
  {
    key: "product_new",
    title: "🆕 Chegou novidade!",
    message: "Acabou de entrar um novo produto no app. Quer ver primeiro? ✨",
    actionType: "custom",
    customData: { type: "custom", route: "/produtos" },
  },
  {
    key: "product_hot",
    title: "⭐ Queridinho do momento",
    message:
      "Um dos mais procurados da semana está em destaque. Dá uma olhada!",
    actionType: "custom",
    customData: { type: "custom", route: "/produtos" },
  },
  {
    key: "product_offer",
    title: "💸 Oportunidade",
    message:
      "Encontramos uma condição melhor em um item em destaque. Veja os detalhes no app.",
    actionType: "custom",
    customData: { type: "custom", route: "/produtos" },
  },
  {
    key: "product_combo",
    title: "🎁 Combo recomendado",
    message:
      "Montamos um combo com ótimo custo-benefício pra você comparar. Quer ver?",
    actionType: "custom",
    customData: { type: "custom", route: "/produtos" },
  },
  {
    key: "product_tip",
    title: "🧠 Dica rápida",
    message:
      "Uma dica simples pode melhorar seus resultados. Veja a recomendação do dia no app.",
    actionType: "custom",
    customData: { type: "custom", route: "/produtos" },
  },

  // =========================
  // NOVO: Histórico / Tarot / Guia Emocional
  // =========================
  {
    key: "tarot_history",
    title: "🃏 Seu Tarot está disponível",
    message:
      "Seu tarot e o histórico de tiragens já estão prontos. Toque para ver agora ✨",
    actionType: "custom",
    customData: { type: "custom", route: "/tarot" },
  },
  {
    key: "emotional_clara",
    title: "🧠 Guia emocional: converse com a Clara",
    message:
      "A Clara está disponível para te ajudar agora. Abra o Guia Emocional e continue sua conversa 💗",
    actionType: "custom",
    customData: { type: "custom", route: "/ai-chat" },
  },
];

// helpers
function isQuickValue(v) {
  return (v || "").startsWith("quick:");
}
function quickKeyFromValue(v) {
  return (v || "").replace("quick:", "").trim();
}
function getQuickTemplateByKey(key) {
  return QUICK_TEMPLATES.find((t) => t.key === key) || null;
}

function normalizeText(v) {
  return (v || "").toString().trim().toLowerCase();
}

function signatureOfTemplate(t) {
  const title = normalizeText(t?.title);
  const message = normalizeText(t?.message);
  return `${title}||${message}`;
}

export default function EnviarNotificacao() {
  const [templates, setTemplates] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [titulo, setTitulo] = useState("");
  const [mensagem, setMensagem] = useState("");

  // 🔥 Tipo de envio
  const [mode, setMode] = useState("user");

  // A) user
  const [selectedUser, setSelectedUser] = useState("");

  // B) topic
  const [topic, setTopic] = useState("all_users");

  // C) segment
  const [segmentPreset, setSegmentPreset] = useState("verified_only");
  const [inactiveDays, setInactiveDays] = useState(7);

  // extras do push
  const [channelId, setChannelId] = useState("fcm_default_channel");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ Ação / deep-link
  const [actionType, setActionType] = useState("none");

  // Campos por ação
  const [matchId, setMatchId] = useState("");
  const [senderId, setSenderId] = useState(""); // chat
  const [otherUserId, setOtherUserId] = useState(""); // match
  const [fromUserId, setFromUserId] = useState(""); // like
  const [suggestionId, setSuggestionId] = useState(""); // suggestion

  // Custom JSON
  const [customDataText, setCustomDataText] = useState(
    '{\n  "type": "custom",\n  "route": "/notifications"\n}'
  );

  // ✅ Busca para o select de templates (melhora UX)
  const [templateSearch, setTemplateSearch] = useState("");

  // ============================================================
  // 🔥 CARREGAR TEMPLATES (Firestore)
  // ============================================================
  async function loadTemplates() {
    const snap = await getDocs(collection(db, "notifications_templates"));
    const arr = [];
    snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));

    // ordena por título
    arr.sort((a, b) =>
      (a.title || "").toString().localeCompare((b.title || "").toString())
    );

    setTemplates(arr);
  }

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // 🔥 CARREGAR USUÁRIOS (pra modo A)
  // ============================================================
  useEffect(() => {
    async function loadUsers() {
      const snap = await getDocs(collection(db, "users"));
      const arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      setUsuarios(arr);
    }
    loadUsers();
  }, []);

  const usuariosOrdenados = useMemo(() => {
    const list = [...usuarios];
    list.sort((a, b) => {
      const an = (a.name || a.nome || "").toString().toLowerCase();
      const bn = (b.name || b.nome || "").toString().toLowerCase();
      return an.localeCompare(bn);
    });
    return list;
  }, [usuarios]);

  // ============================================================
  // ✅ Remove duplicados no dropdown
  // - se você instalou quick_* no Firestore, eles não aparecem 2x
  // - além do id quick_*, também filtra por assinatura title+message
  // ============================================================
  const firestoreTemplatesFiltered = useMemo(() => {
    const quickIds = new Set(QUICK_TEMPLATES.map((t) => `quick_${t.key}`));
    const quickSignatures = new Set(QUICK_TEMPLATES.map(signatureOfTemplate));

    const filtered = templates.filter((t) => {
      if (!t?.id) return false;

      // 1) remove docs quick_*
      if (quickIds.has(t.id)) return false;

      // 2) remove docs com mesmo texto (título+mensagem) de algum quick
      const sig = signatureOfTemplate(t);
      if (quickSignatures.has(sig)) return false;

      return true;
    });

    return filtered;
  }, [templates]);

  // ============================================================
  // ✅ Filtro de busca no dropdown (rápidos + firestore filtrado)
  // ============================================================
  const filteredQuickTemplates = useMemo(() => {
    const q = normalizeText(templateSearch);
    if (!q) return QUICK_TEMPLATES;

    return QUICK_TEMPLATES.filter((t) => {
      return (
        normalizeText(t.title).includes(q) || normalizeText(t.message).includes(q)
      );
    });
  }, [templateSearch]);

  const filteredFirestoreTemplates = useMemo(() => {
    const q = normalizeText(templateSearch);
    if (!q) return firestoreTemplatesFiltered;

    return firestoreTemplatesFiltered.filter((t) => {
      return (
        normalizeText(t.title).includes(q) || normalizeText(t.message).includes(q)
      );
    });
  }, [templateSearch, firestoreTemplatesFiltered]);

  const templatesVisibleCount = useMemo(() => {
    return (filteredQuickTemplates?.length || 0) + (filteredFirestoreTemplates?.length || 0);
  }, [filteredQuickTemplates, filteredFirestoreTemplates]);

  // ============================================================
  // 📌 Aplicar TEMPLATE (Firestore ou Rápido)
  // ============================================================
  async function handleTemplateSelect(value) {
    setSelectedTemplate(value);

    if (!value) {
      setTitulo("");
      setMensagem("");
      return;
    }

    // ✅ Templates rápidos (sistema)
    if (isQuickValue(value)) {
      const key = quickKeyFromValue(value);
      const tpl = getQuickTemplateByKey(key);
      if (!tpl) return;

      setTitulo(tpl.title || "");
      setMensagem(tpl.message || "");

      // Ajusta ação automaticamente
      const nextAction = (tpl.actionType || "none").toString();
      setActionType(nextAction);

      // Zera campos de ids (pra evitar "lixo")
      setMatchId("");
      setSenderId("");
      setOtherUserId("");
      setFromUserId("");
      setSuggestionId("");

      // Se for custom, injeta o customData se existir
      if (nextAction === "custom") {
        const obj =
          tpl.customData && typeof tpl.customData === "object"
            ? tpl.customData
            : { type: "custom", route: "/notifications" };
        setCustomDataText(JSON.stringify(obj, null, 2));
      }

      return;
    }

    // ✅ Templates do Firestore
    const ref = doc(db, "notifications_templates", value);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      const d = snap.data();

      setTitulo(d.title || "");
      setMensagem(d.message || "");

      // Se o doc salvo tiver actionType/customData, aplica também (retrocompatível)
      const savedAction = (d.actionType || "").toString().trim();
      const savedCustomData = d.customData || null;

      if (savedAction) {
        setActionType(savedAction);

        // Limpa campos de ids
        setMatchId("");
        setSenderId("");
        setOtherUserId("");
        setFromUserId("");
        setSuggestionId("");

        if (savedAction === "custom") {
          const obj =
            savedCustomData && typeof savedCustomData === "object"
              ? savedCustomData
              : { type: "custom", route: "/notifications" };
          setCustomDataText(JSON.stringify(obj, null, 2));
        }
      }
    }
  }

  // ============================================================
  // ✅ Instalar templates rápidos no Firestore (1 clique)
  // - Usa ids estáveis: quick_<key>
  // - Salva actionType/customData junto (pra reaproveitar depois)
  // ============================================================
  async function installQuickTemplatesInFirestore() {
    setLoading(true);
    try {
      for (const t of QUICK_TEMPLATES) {
        const id = `quick_${t.key}`;
        await setDoc(
          doc(db, "notifications_templates", id),
          {
            title: t.title,
            message: t.message,
            actionType: t.actionType || "none",
            customData:
              t.actionType === "custom" && t.customData ? t.customData : null,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await loadTemplates();
      alert("✅ Templates rápidos instalados/atualizados no Firestore!");
    } catch (e) {
      console.error(e);
      alert("❌ Erro ao instalar templates no Firestore. Veja o console.");
    } finally {
      setLoading(false);
    }
  }

  // ============================================================
  // ✅ Monta extras (data) para deep-link no mobile
  // ============================================================
  function buildExtraData() {
    if (actionType === "none") {
      return { type: "push", route: "/notifications" };
    }

    if (actionType === "chat") {
      return {
        type: "chat",
        route: "/chat",
        matchId: (matchId || "").trim(),
        senderId: (senderId || "").trim(),
      };
    }

    if (actionType === "match") {
      return {
        type: "match",
        route: "/match",
        otherUserId: (otherUserId || "").trim(),
      };
    }

    if (actionType === "like") {
      return {
        type: "like",
        route: "/likes",
        fromUserId: (fromUserId || "").trim(),
      };
    }

    if (actionType === "verified") {
      return { type: "verified", route: "/profile" };
    }

    if (actionType === "suggestion") {
      return {
        type: "suggestion",
        route: "/suggestions",
        suggestionId: (suggestionId || "").trim(),
      };
    }

    if (actionType === "custom") {
      try {
        const obj = JSON.parse(customDataText || "{}");
        return obj && typeof obj === "object" ? obj : { type: "custom" };
      } catch (_) {
        return { type: "custom", route: "/notifications", invalidJson: true };
      }
    }

    return { type: "push", route: "/notifications" };
  }

  // ============================================================
  // ✅ Monta payload do JOB (conforme sendNotificationJob)
  // ============================================================
  function buildJobPayload() {
    const title = (titulo || "").trim();
    const message = (mensagem || "").trim();

    const payload = {
      title,
      message,
      mode, // user | topic | segment
      channelId: (channelId || "fcm_default_channel").trim(),
      status: "pending",
      createdAt: serverTimestamp(),
      data: buildExtraData(),
    };

    if (mode === "user") payload.targetUserId = selectedUser;
    if (mode === "topic") payload.topic = (topic || "").trim();

    if (mode === "segment") {
      const seg = { preset: segmentPreset };
      if (segmentPreset === "inactive_days") {
        seg.inactiveDays = Number(inactiveDays || 7);
      }
      payload.segment = seg;
    }

    return payload;
  }

  // ============================================================
  // ✅ Valida extras conforme ação
  // ============================================================
  function validateActionExtras() {
    if (actionType === "chat") {
      if (!(matchId || "").trim()) {
        alert("Para ação CHAT, informe o matchId.");
        return false;
      }
      return true;
    }

    if (actionType === "match") {
      if (!(otherUserId || "").trim()) {
        alert("Para ação MATCH, informe o otherUserId.");
        return false;
      }
      return true;
    }

    if (actionType === "like") {
      if (!(fromUserId || "").trim()) {
        alert("Para ação LIKE, informe o fromUserId.");
        return false;
      }
      return true;
    }

    if (actionType === "suggestion") {
      if (!(suggestionId || "").trim()) {
        alert("Para ação SUGGESTION, informe o suggestionId.");
        return false;
      }
      return true;
    }

    if (actionType === "custom") {
      try {
        JSON.parse(customDataText || "{}");
        return true;
      } catch (_) {
        alert("JSON inválido no Custom Data. Corrija antes de enviar.");
        return false;
      }
    }

    return true;
  }

  // ============================================================
  // 🚀 ENVIAR (cria 1 documento em notifications_jobs)
  // ============================================================
  async function enviar() {
    const title = (titulo || "").trim();
    const msg = (mensagem || "").trim();

    if (!title || !msg) {
      alert("Preencha o título e a mensagem.");
      return;
    }

    if (mode === "user" && !selectedUser) {
      alert("Selecione um usuário para enviar.");
      return;
    }

    if (mode === "topic" && !(topic || "").trim()) {
      alert("Informe o tópico (ex: all_users).");
      return;
    }

    if (mode === "segment" && segmentPreset === "inactive_days") {
      const d = Number(inactiveDays || 0);
      if (!Number.isFinite(d) || d <= 0) {
        alert("Informe um número válido de dias de inatividade.");
        return;
      }
    }

    if (!validateActionExtras()) return;

    setLoading(true);
    try {
      const payload = buildJobPayload();
      await addDoc(collection(db, "notifications_jobs"), payload);

      alert("✅ Job criado! A Cloud Function vai processar e enviar o push.");

      setTitulo("");
      setMensagem("");
      setSelectedTemplate("");
      setPreviewOpen(false);

      // limpa extras por segurança
      setActionType("none");
      setMatchId("");
      setSenderId("");
      setOtherUserId("");
      setFromUserId("");
      setSuggestionId("");

      // mantém customDataText como default
      setCustomDataText(
        '{\n  "type": "custom",\n  "route": "/notifications"\n}'
      );

      // mantém busca como está (melhor UX), mas se quiser limpar:
      // setTemplateSearch("");
    } catch (e) {
      console.error(e);
      alert("❌ Erro ao criar job. Veja o console.");
    } finally {
      setLoading(false);
    }
  }

  // ============================================================
  // MODAL DE PREVIEW
  // ============================================================
  function ModalPreview() {
    if (!previewOpen) return null;

    const extraData = buildExtraData();
    const selectedUserObj = usuariosOrdenados.find((u) => u.id === selectedUser);
    const selectedUserName = selectedUserObj?.name || selectedUserObj?.nome || "";

    return (
      <div className="modal-bg">
        <div className="modal-content">
          <h3>Pré-visualização da notificação</h3>

          <div className="preview-box">
            <h4>{titulo || "Título vazio"}</h4>
            <p>{mensagem || "Mensagem vazia"}</p>

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
              <div>
                <b>Modo:</b>{" "}
                {MODE_OPTIONS.find((m) => m.value === mode)?.label || mode}
              </div>

              {mode === "user" && (
                <div>
                  <b>Usuário:</b>{" "}
                  {selectedUser
                    ? `${selectedUserName ? selectedUserName + " — " : ""}${selectedUser}`
                    : "Nenhum (selecione um usuário)"}
                </div>
              )}

              {mode === "topic" && (
                <div>
                  <b>Tópico:</b> {topic || "—"}
                </div>
              )}

              {mode === "segment" && (
                <div>
                  <b>Segmento:</b>{" "}
                  {SEGMENT_PRESETS.find((s) => s.value === segmentPreset)?.label ||
                    segmentPreset}
                  {segmentPreset === "inactive_days" ? (
                    <>
                      {" "}
                      (<b>{inactiveDays}</b> dias)
                    </>
                  ) : null}
                </div>
              )}

              <div>
                <b>Canal:</b> {channelId || "fcm_default_channel"}
              </div>

              <div style={{ marginTop: 10 }}>
                <b>Ação / Extras (data):</b>
                <pre className="preview-json">
                  {JSON.stringify(extraData, null, 2)}
                </pre>
              </div>
            </div>
          </div>

          <button className="btn-close" onClick={() => setPreviewOpen(false)}>
            Fechar
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // UI auxiliar por modo
  // ============================================================
  function renderModeFields() {
    if (mode === "user") {
      return (
        <>
          <label>Enviar para (usuário específico)</label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="form-select"
          >
            <option value="">Selecione um usuário</option>
            {usuariosOrdenados.map((u) => (
              <option key={u.id} value={u.id}>
                {(u.name || u.nome || "Usuário")} ({u.id})
              </option>
            ))}
          </select>

          <small className="hint">
            💡 Envia apenas para o UID selecionado (usa o <b>fcmToken</b> do usuário).
          </small>
        </>
      );
    }

    if (mode === "topic") {
      return (
        <>
          <label>Tópico (broadcast)</label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Ex: all_users ou weekly"
            className="form-input"
          />
          <small className="hint">
            💡 Para receber, o app precisa se inscrever no tópico com{" "}
            <b>subscribeToTopic</b>.
          </small>
        </>
      );
    }

    if (mode === "segment") {
      return (
        <>
          <label>Segmento</label>
          <select
            value={segmentPreset}
            onChange={(e) => setSegmentPreset(e.target.value)}
            className="form-select"
          >
            {SEGMENT_PRESETS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {segmentPreset === "inactive_days" && (
            <>
              <label>Inativos há quantos dias?</label>
              <input
                type="number"
                min="1"
                value={inactiveDays}
                onChange={(e) => setInactiveDays(Number(e.target.value || 7))}
                placeholder="Ex: 7"
                className="form-input"
              />
              <small className="hint">
                💡 Usa o campo <b>lastActiveAt</b> (Timestamp) do usuário.
              </small>
            </>
          )}

          {segmentPreset === "verified_only" && (
            <small className="hint">
              💡 Envia para quem tem <b>verificationStatus = verified</b>.
            </small>
          )}

          {segmentPreset === "premium_only" && (
            <small className="hint">
              💡 Envia para quem for detectado como premium
              (isPremium/premium/planoNome/premiumUntil…).
            </small>
          )}
        </>
      );
    }

    return null;
  }

  // ============================================================
  // UI auxiliar: extras / deep link
  // ============================================================
  function renderActionFields() {
    return (
      <>
        <label>Ação ao clicar (Deep-link / Extras)</label>
        <select value={actionType} onChange={(e) => setActionType(e.target.value)} className="form-select">
          {ACTION_OPTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>

        {actionType === "chat" && (
          <>
            <label>matchId (obrigatório)</label>
            <input
              type="text"
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
              placeholder="Ex: docId do match"
              className="form-input"
            />
            <label>senderId (opcional)</label>
            <input
              type="text"
              value={senderId}
              onChange={(e) => setSenderId(e.target.value)}
              placeholder="Ex: uid_do_remetente (opcional)"
              className="form-input"
            />
          </>
        )}

        {actionType === "match" && (
          <>
            <label>otherUserId (obrigatório)</label>
            <input
              type="text"
              value={otherUserId}
              onChange={(e) => setOtherUserId(e.target.value)}
              placeholder="Ex: uid_do_outro_usuario"
              className="form-input"
            />
          </>
        )}

        {actionType === "like" && (
          <>
            <label>fromUserId (obrigatório)</label>
            <input
              type="text"
              value={fromUserId}
              onChange={(e) => setFromUserId(e.target.value)}
              placeholder="Ex: uid_de_quem_curtiu"
              className="form-input"
            />
          </>
        )}

        {actionType === "suggestion" && (
          <>
            <label>suggestionId (obrigatório)</label>
            <input
              type="text"
              value={suggestionId}
              onChange={(e) => setSuggestionId(e.target.value)}
              placeholder="Ex: docId da sugestão"
              className="form-input"
            />
          </>
        )}

        {actionType === "verified" && (
          <small className="hint">
            💡 Vai enviar <b>data.type=verified</b>.
          </small>
        )}

        {actionType === "custom" && (
          <>
            <label>Custom Data (JSON)</label>
            <textarea
              value={customDataText}
              onChange={(e) => setCustomDataText(e.target.value)}
              placeholder='{"type":"custom","route":"/notifications"}'
              className="json-area form-textarea"
            />
            <small className="hint">
              💡 Dica: para abrir Tarot use <b>{`{"type":"custom","route":"/tarot"}`}</b> e para a Clara{" "}
              <b>{`{"type":"custom","route":"/ai-chat"}`}</b>.
            </small>
          </>
        )}
      </>
    );
  }

  return (
    <div className="notificacao-page-wrapper">
      <Sidebar />

      <div className="notificacao-main-content">
        <Topbar />

        <div className="notificacao-content-container">
          <div className="page-title-row">
            <h2>📢 Enviar Notificação</h2>
            <div className="mini-badges">
              <span className="badge-soft">
                ⚡ {filteredQuickTemplates.length} rápidos
              </span>
              <span className="badge-soft">
                📚 {filteredFirestoreTemplates.length} Firestore
              </span>
              <span className="badge-soft strong">
                🔎 {templatesVisibleCount} visíveis
              </span>
            </div>
          </div>

          <div className="form-card-compact">
            <div className="template-head">
              <label>Template</label>

              <input
                className="template-search"
                type="text"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Buscar template por título ou mensagem..."
              />
            </div>

            <select
              value={selectedTemplate}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="form-select template-select"
            >
              <option value="">Selecione um template</option>

              <optgroup label="⚡ Templates rápidos (sistema)">
                {filteredQuickTemplates.map((t) => (
                  <option key={t.key} value={`quick:${t.key}`}>
                    {t.title}
                  </option>
                ))}
              </optgroup>

              <optgroup label="📚 Templates salvos (Firestore) — sem duplicados">
                {filteredFirestoreTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
            </select>

            <div className="actions-row">
              <button
                type="button"
                className="btn-soft"
                onClick={installQuickTemplatesInFirestore}
                disabled={loading}
                title="Cria/atualiza os templates rápidos dentro de notifications_templates"
              >
                {loading ? "Instalando..." : "➕ Instalar templates rápidos no Firestore"}
              </button>

              <button
                type="button"
                className="btn-soft"
                onClick={() => loadTemplates()}
                disabled={loading}
                title="Recarrega a lista de templates salvos"
              >
                🔄 Recarregar templates
              </button>

              <button
                type="button"
                className="btn-soft danger"
                onClick={() => {
                  setSelectedTemplate("");
                  setTitulo("");
                  setMensagem("");
                  setActionType("none");
                  setMatchId("");
                  setSenderId("");
                  setOtherUserId("");
                  setFromUserId("");
                  setSuggestionId("");
                  setCustomDataText(
                    '{\n  "type": "custom",\n  "route": "/notifications"\n}'
                  );
                }}
                disabled={loading}
                title="Limpa os campos do formulário"
              >
                🧹 Limpar
              </button>
            </div>

            <hr className="divider" />

            <div className="form-section">
              <label>Tipo de envio</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} className="form-select">
                {MODE_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>

              {renderModeFields()}
            </div>

            <div className="form-section">
              <label>Canal (Android)</label>
              <input
                type="text"
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                placeholder="Ex: fcm_default_channel"
                className="form-input"
              />
              <small className="hint">
                💡 Deve bater com o channel do Android (Manifest / Local Notifications).
              </small>
            </div>

            <div className="form-section">
              {renderActionFields()}
            </div>

            <div className="form-section">
              <label>Título</label>
              <input
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Digite o título"
                className="form-input"
              />
            </div>

            <div className="form-section">
              <label>Mensagem</label>
              <textarea
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                placeholder="Escreva a mensagem da notificação"
                className="form-textarea"
              />
            </div>

            <div className="btn-group">
              <button
                type="button"
                className="btn-preview"
                onClick={() => setPreviewOpen(true)}
              >
                Visualizar
              </button>

              <button
                type="button"
                className="btn-send"
                onClick={enviar}
                disabled={loading}
              >
                {loading ? "Enviando..." : "Enviar Notificação"}
              </button>
            </div>
          </div>

          <ModalPreview />
        </div>
      </div>
    </div>
  );
}  